import { Notify } from "@/components/ui/notify";
import { formatDisplay } from "@/lib/datetime-format";
import type { AppliedScenario } from "@/types/applied-scenario.types";
import { useAppliedScenario } from "./hooks/useAppliedScenario";
import { SettlementColumns } from "./SettlementColumns";
import { ExternalInterferenceList } from "./ExternalInterferenceList";
import { AdherencePanel } from "./AdherencePanel";

interface SettlementSectionProps {
  scenario: AppliedScenario;
}

// A apuração aberta de UM compromisso.
// Por que uma consulta de DETALHE em vez de usar o que a lista já trouxe: a lista NÃO verifica a
// interferência externa (`externalInterference` vem nulo lá), porque varrer a produção da janela de
// cada compromisso da linha seria caro e a maioria nunca é aberta. Aqui, sim — e a diferença entre
// "não verifiquei" e "verifiquei e não há" é exatamente o que a regra exige preservar.

export function SettlementSection({ scenario }: SettlementSectionProps) {
  const detail = useAppliedScenario(scenario.id);

  if (detail.isError) {
    return (
      <Notify.QueryError
        error={detail.error}
        onRetry={() => void detail.refetch()}
        title="Não foi possível carregar a apuração"
      />
    );
  }
  if (detail.isPending) {
    return <p className="text-sm text-muted-foreground">Carregando a apuração…</p>;
  }

  const settlement = detail.data.settlement;
  if (!settlement) {
    return (
      <Notify.Empty
        variant="inline"
        title="Sem apuração"
        description="Este compromisso ainda não foi apurado."
      />
    );
  }

  return (
    <div className="space-y-4 rounded-md border border-border-subtle p-3">
      <p className="text-[12px] text-text-secondary">
        Apurado em {formatDisplay(settlement.settledAt)}.
      </p>
      <SettlementColumns metrics={settlement.metrics} />
      {/* A aderência CONGELADA ao lado das três colunas, e a elegibilidade DECLARADA.
          Sem ela, um desvio pequeno com execução fora do combinado seria lido como acerto do gêmeo. */}
      <AdherencePanel
        adherence={detail.data.adherence}
        thresholdPct={detail.data.adherenceThresholdPct}
      />
      {detail.data.countsTowardAccuracy === false ? (
        <p className="text-[12px] text-text-secondary">
          Este compromisso fica <span className="text-text-primary">fora</span> da estatística de
          acerto do gêmeo: a operação não seguiu o combinado dentro do limiar declarado, então
          comparar previsto e realizado aqui julgaria a previsão por uma execução diferente.
        </p>
      ) : null}
      <ExternalInterferenceList interference={settlement.externalInterference} />
    </div>
  );
}
